import { ZipApiTimeoutError } from './ZipApiTimeoutError';

export class ZipApiError extends Error {
  constructor(public response: Response, message?: string) {
    super(message || `${response.status} ${response.statusText}`);
  }
}

export async function getApiErrorMessage(error: any, context: string): Promise<string> {
  if (error instanceof ZipApiTimeoutError) {
    return `${context}: the request timed out. Please try again.`;
  }
  if (error instanceof ZipApiError && error.response) {
    if (error.response.status === 504) {
      return `${context}: the request timed out. Please try again.`;
    }
    try {
      const body = await error.response.json();
      const baseMessage = body.message || body.error;
      // Content Graph credentials need time to replicate after initial setup,
      // causing temporary 401s. Show a friendlier message so users know to wait.
      if (error.response.status === 401 && baseMessage && baseMessage.includes('Graph unauthorized')) {
        return `${context}: Graph authentication is being set up. This usually takes a few minutes — please try again shortly.`;
      }

      // google.rpc.BadRequest validation errors carry per-field failures in
      // details[].field_violations. Enumerate them so the user sees exactly
      // which fields failed instead of a generic "Validation failed" (OCP-1988).
      const violations = extractFieldViolations(body);
      if (violations.length > 0) {
        const list = violations.map((violation) => `• ${violation}`).join('\n');
        const prefix = baseMessage ? `${context}: ${baseMessage}` : context;
        return `${prefix}\n${list}`;
      }

      if (baseMessage) {
        return `${context}: ${baseMessage}`;
      }
    } catch {
      // response body wasn't JSON, use fallback
    }
  }
  // Client-side Errors carry user-facing messages; ZipApiError/ZipApiTimeoutError messages do not.
  if (error instanceof Error && !(error instanceof ZipApiError) && !(error instanceof ZipApiTimeoutError)
    && error.message) {
    return `${context}: ${error.message}`;
  }
  return `${context}: an unexpected error occurred.`;
}

/**
 * Flattens google.rpc.BadRequest field violations into "field: description"
 * strings. Returns an empty array when the body carries no violations.
 */
function extractFieldViolations(body: any): string[] {
  const violations: string[] = [];
  if (!Array.isArray(body?.details)) {
    return violations;
  }
  for (const detail of body.details) {
    const fieldViolations = detail?.field_violations;
    if (!Array.isArray(fieldViolations)) {
      continue;
    }
    for (const violation of fieldViolations) {
      const field = violation?.field;
      const description = violation?.description ?? '';
      violations.push(field ? `${field}: ${description}` : description);
    }
  }
  return violations;
}
